import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatPrice } from "../lib/api";
import HolographicPlaceholder from "../components/HolographicPlaceholder";
import ProductCard from "../components/ProductCard";
import { ShieldCheck, Truck, Sparkles, Plus, Minus, ArrowLeft } from "lucide-react";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const { add } = useCart();

  useEffect(() => {
    api.get(`/products/${slug}`).then(({ data }) => setProduct(data)).catch(() => setProduct(null));
  }, [slug]);

  useEffect(() => {
    if (product?.category) {
      api.get(`/products?category=${product.category}`)
        .then(({ data }) => setRelated(data.filter((p) => p.slug !== slug).slice(0, 4)))
        .catch(() => {});
    }
  }, [product, slug]);

  if (!product) {
    return <div className="max-w-[1400px] mx-auto px-6 py-20 text-white/60">Loading product…</div>;
  }

  const onAdd = () => {
    add(product, qty);
    toast.success(`${product.name} × ${qty} added to cart`);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-12">
      <Link to="/shop" data-testid="product-back" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white mb-8">
        <ArrowLeft size={14} /> Back to shop
      </Link>

      <div className="grid grid-cols-12 gap-12">
        <div className="col-span-12 lg:col-span-6">
          <div className="sticky top-32">
            <HolographicPlaceholder label={product.name} subLabel={product.rarity || product.category} ratio="aspect-[3/4]" imageUrl={product.image_url} />
          </div>
        </div>

        <div className="col-span-12 lg:col-span-6 max-w-xl">
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#B026FF] font-bold">
            {product.category?.replace(/-/g, " ")}
          </div>
          <h1 className="mt-3 font-display font-extrabold text-3xl lg:text-5xl tracking-tighter text-white leading-[1.05]">
            {product.name}
          </h1>

          <div className="mt-6 flex items-baseline gap-3">
            <div className="font-mono text-3xl text-white">{formatPrice(product.price_cents)}</div>
            {product.old_price_cents && (
              <div className="font-mono text-base text-white/40 line-through">{formatPrice(product.old_price_cents)}</div>
            )}
          </div>

          <p className="mt-6 text-white/70 leading-relaxed">{product.description}</p>

          {product.rarity && (
            <div className="mt-6 inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5 text-xs text-white/70 font-mono">
              <Sparkles size={12} className="text-[#B026FF]" /> {product.rarity}
            </div>
          )}

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-white/10 rounded-md">
              <button
                data-testid="product-qty-decr"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="px-3 py-3 text-white/70 hover:text-white"
                aria-label="Decrease quantity"
              >
                <Minus size={14} />
              </button>
              <span className="px-4 font-mono">{qty}</span>
              <button
                data-testid="product-qty-incr"
                onClick={() => setQty((q) => q + 1)}
                className="px-3 py-3 text-white/70 hover:text-white"
                aria-label="Increase quantity"
              >
                <Plus size={14} />
              </button>
            </div>
            <button
              onClick={onAdd}
              data-testid="product-add-to-cart"
              className="btn-violet flex-1 h-12 rounded-md font-medium tracking-wide"
            >
              Add to Cart — {formatPrice(product.price_cents * qty)}
            </button>
          </div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 p-4 border border-white/10 rounded-lg bg-white/[0.02]">
              <Truck size={18} className="text-[#B026FF] mt-0.5" />
              <div>
                <div className="text-sm font-medium text-white">Tracked shipping</div>
                <div className="text-xs text-white/50 mt-1">Free over £50, worldwide options</div>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 border border-white/10 rounded-lg bg-white/[0.02]">
              <ShieldCheck size={18} className="text-[#B026FF] mt-0.5" />
              <div>
                <div className="text-sm font-medium text-white">Authenticated</div>
                <div className="text-xs text-white/50 mt-1">Sealed verification, 30-day returns</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-32">
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#B026FF] font-bold mb-3">You may also like</div>
          <h2 className="font-display font-light text-3xl lg:text-4xl tracking-tight text-white">More from this collection</h2>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
